import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1759646881002-33ce4a8bf3d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxoaWdoJTIwZmFzaGlvbiUyMG1vZGVsJTIwZGFya3xlbnwwfHx8fDE3ODExMDk0NTl8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      {/* Background image — right side dominant */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="DenimGlow couture editorial"
          className="absolute right-0 top-0 h-full w-full md:w-[70%] object-cover object-center"
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0b10] via-[#0a0b10]/85 md:via-[#0a0b10]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b10] via-transparent to-transparent" />
        {/* Denim wash */}
        <div className="absolute right-0 bottom-0 w-[60%] h-[60%] bg-[radial-gradient(circle_at_70%_70%,rgba(44,82,130,0.18),transparent_60%)]" />
      </div>

      {/* Left vertical hairline */}
      <div className="hidden md:block absolute left-12 top-0 bottom-0 w-px bg-white/8 z-10" />
      <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 vertical-text text-[10px] tracking-[0.5em] uppercase text-silver/80">
        DenimGlow · MMXXVI
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center px-6 md:px-12 lg:px-24 pt-32 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7 lg:col-span-6"
          >
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">
              Maison Asymmetry · Summer Edit 26
            </div>

            <h1
              data-testid="hero-headline"
              className="font-serif text-white text-5xl sm:text-7xl md:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tighter"
            >
              Crafted
              <br />
              for the <span className="italic text-silver/90">Bold.</span>
              <br />
              Made <span className="text-denim-light">to Glow.</span>
            </h1>

            <p
              data-testid="hero-subtext"
              className="mt-8 max-w-md text-white/65 text-base md:text-lg font-light leading-relaxed"
            >
              A house of artistic luxury — apparel, jewellery, handbags, and
              accessories, reimagined through asymmetry, denim, and the
              quiet shimmer of metal.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4 sm:items-center">
              <a
                href="#collections"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 bg-white text-black px-9 py-4 uppercase tracking-[0.32em] text-[11px] hover:bg-silver transition-all duration-500"
              >
                Explore DenimGlow
                <ArrowDownRight
                  size={14}
                  strokeWidth={1.4}
                  className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform"
                />
              </a>
              <a
                href="#story"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-9 py-4 uppercase tracking-[0.32em] text-[11px] hover:border-white hover:bg-white/5 transition-all duration-500"
              >
                The House
              </a>
            </div>
          </motion.div>

          {/* Right meta column */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1.2 }}
            className="hidden md:flex md:col-span-5 lg:col-span-6 items-end justify-end"
          >
            <div className="text-right space-y-3 max-w-[260px]">
              <div className="eyebrow text-white/55">The House</div>
              <div className="font-serif text-2xl text-white leading-tight">
                Four edits.
                <br />
                One Maison.
              </div>
              <div className="text-silver text-xs tracking-[0.3em] uppercase">
                Hand-finished · Milano
              </div>
              <div className="h-px w-24 bg-white/20 ml-auto mt-4" />
              <div className="text-white/70 text-xs tracking-[0.25em] uppercase">
                Summer · MMXXVI
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <span className="text-[10px] tracking-[0.45em] uppercase text-white/55">
          Scroll
        </span>
        <div className="relative h-10 w-px bg-white/15 overflow-hidden">
          <span className="absolute top-0 left-0 w-px h-3 bg-silver scroll-cue-dot" />
        </div>
      </div>
    </section>
  );
}
